import { GriffelStyle } from "@griffel/core";
import { makeStyles, tokens } from "@fluentui/react-components";
import { SendBoxStylesProps } from "@azure/communication-react";
import { pcpTokens } from "../../styles";

const backgroundColor = 'white';

const zeroBorder = {
    borderBottomWidth: '0',
    border: '0',
};

const borderRadiusStyle: GriffelStyle = {
    borderRadius: '25px',
};

const baseInputRootStyle: GriffelStyle = {
    border: '0',
    backgroundColor: "transparent",

    '&::after': {
        ...zeroBorder,
    },
};

const enabledInputStyle: GriffelStyle = {
    border: `1px solid ${pcpTokens.pcpColorNeutralStroke2}`,
    borderTopColor: pcpTokens.pcpColorNeutralStroke2,
    borderRightColor: pcpTokens.pcpColorNeutralStroke2,
    borderBottomColor: pcpTokens.pcpColorNeutralStroke2,
    borderLeftColor: pcpTokens.pcpColorNeutralStroke2,
};

const enabledHoverInputStyle: GriffelStyle = {
    border: '1px solid #484E59',
    borderTopColor: '#484E59',
    borderRightColor: '#484E59',
    borderBottomColor: '#484E59',
    borderLeftColor: '#484E59',
};

const enabledActiveInputStyle: GriffelStyle = {
    background: `linear-gradient(white, white) padding-box, linear-gradient(270deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackground2} 39%) border-box`,
    borderRadius: '25px',
    border: '1px solid transparent',

    // NOTE: this seems redundant but is required,
    //             as some components do use more specific selectors
    //             which would override the above border colors
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
};

const disabledInputStyle: GriffelStyle = {
    border: '1px solid #C8D2E7',
};

export const listboxOverlap = 22;          // overlap between listbox and trigger button

/**
 * Themed text input field.
 */
export const usePcpInputStyles = makeStyles({

    root: baseInputRootStyle,

    input: {
        ...borderRadiusStyle,
        backgroundColor: backgroundColor,

        '&:enabled': {
            ...enabledInputStyle,
            '&:hover': {
                ...enabledHoverInputStyle,
            },
        },

        '&:focus, &:focus:hover': {
            ...enabledActiveInputStyle,
        },

        '&:disabled': {
            ...disabledInputStyle,
        },

        '&::after': {
            borderBottomWidth: '0',
            border: '0',
        },
    }
});

/**
 * Themed SearchBox input field.
 */
export const usePcpSearchBoxStyles = makeStyles({

    root: {
        ...enabledInputStyle,
        ...borderRadiusStyle,
        backgroundColor: backgroundColor,

        '&::after': {
            ...zeroBorder,
        },

        '&:enabled': {
            ...enabledInputStyle,
        },

        '&:hover': {
            ...enabledHoverInputStyle,
        },

        '&:active, &:focus, &:focus:hover, &:focus-within': {
            ...enabledActiveInputStyle,
        },

        '&:disabled': {
            ...disabledInputStyle,
        },
    },

    input: {
        ...zeroBorder,
    }
});

/**
 * Themed dropdown input field.
 */
export const usePcpDropdownStyles = makeStyles({

    root: {
        ...baseInputRootStyle,
        width: '100%',
        position: 'relative',   // required for zIndex to activate (in PcpDropdown.tsx)
    },

    // = dropdown window / options list
    listbox: {
        backgroundColor: '#F9FAFB',
    },
    listboxOpenAbove: {
        // padding-bottom absorbs the overlap (listbox bottom overlaps trigger top)
        padding: `8px 8px ${8 + listboxOverlap}px 8px`,
    },
    listboxOpenBelow: {
        // padding-top absorbs the overlap (listbox top overlaps trigger bottom)
        padding: `${8 + listboxOverlap}px 8px 8px 8px`,
    },

    // these styles allow option text to break and wrap on long words, e.g. emails
    optionText: {
        overflow: "hidden",
        overflowWrap: "break-word",
    },

    button: {
        ...borderRadiusStyle,
        width: '100%',
        backgroundColor: backgroundColor,

        '&:enabled': {
            ...enabledInputStyle,
            '&:hover': {
                ...enabledHoverInputStyle,
            },
        },

        '&[aria-expanded="true"], &[aria-expanded="true"]:hover, &:focus-within:active': {
            ...enabledActiveInputStyle,
        },

        '&:disabled': {
            ...disabledInputStyle,
        },

        '&::after': {
            ...zeroBorder,
        },
    }
});

export const usePcpDropdownOptionStyles = makeStyles({
    root: {
        '&[data-activedescendant], &[aria-selected="true"]': {
            background: `linear-gradient(270deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackground2} 39%)`,
            borderRadius: '6px',
            color: 'white',

            '&:hover': {
                color: 'white',
            },
        },
    },
});


export const sendBoxStyles: SendBoxStylesProps = {
    root: {
        border: `1px solid ${pcpTokens.pcpColorNeutralStroke2}`,
        borderRadius: '25px',

        '&::after': {
            ...zeroBorder,
        },

        '&:enabled': {
            ...enabledInputStyle,
            '&:hover': {
                ...enabledHoverInputStyle,
            },
        },

        '&:focus-within': {
            ...enabledActiveInputStyle,
        },
    },

    textField: {
        backgroundColor: 'white',
        borderRadius: '25px',
        border: '0',
        paddingLeft: '10px',

        'textarea::placeholder': {
            color: pcpTokens.pcpColorNeutralStroke2,
        },
    },

    sendMessageIconContainer: {
        color: pcpTokens.pcpColorNeutralStroke2,
        '&:hover': {
            color: tokens.colorBrandBackground, // #1088F9
            cursor: 'pointer',
        },
    },
};

